import {
  type BasketReportRow,
  type DeadStockRow,
  type ExpiringLotRow,
  MARKET_REPORT_GROUPINGS,
  type MarketReportGrouping,
  marketReportGroupingSchema,
  type SalesReportRow,
  type TurnoverRow,
} from "@station/contracts";
import { addDays, type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Card,
  Checkbox,
  DataTable,
  type DataTableColumn,
  DateRangePicker,
  EmptyState,
  FormField,
  Input,
  MoneyText,
  Select,
  SkeletonRows,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChartColumnBig } from "lucide-react";
import { type ReactElement, type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../features/counterparties/shared.js";
import { ChartWithTable } from "../../../features/fuel/shared.js";
import { formatStockQuantity, moneyExport, quantityExport } from "../../../features/market/market-values.js";
import { formatPercent } from "../../../features/market/pricing-values.js";
import {
  type MarketReportPeriod,
  useBasketReport,
  useDeadStockReport,
  useExpiringLotsReport,
  useSalesReport,
  useTurnoverReport,
} from "../../../features/market/purchasing-queries.js";
import {
  basketChartOption,
  formatRatio,
  MARKET_REPORT_TABS,
  type MarketReportTab,
  salesChartOption,
  topByRevenue,
} from "../../../features/market/report-values.js";
import {
  MarketPage,
  marketSearchShape,
  MarketStationField,
  NoStations,
  useMarketStations,
} from "../../../features/market/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel } from "../../../lib/excel.js";
import { formatCount, formatDate } from "../../../lib/format.js";

const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...marketSearchShape,
  tab: z.enum(MARKET_REPORT_TABS).optional().catch(undefined),
  from: localDateText,
  to: localDateText,
  // `groupBy` adı muhasebe raporlarında başka enum'la kullanılıyor: arama parametreleri tek tipte birleştiği için ayrı ad.
  breakdown: marketReportGroupingSchema.optional().catch(undefined),
  days: z.coerce.number().int().min(0).max(365).optional().catch(undefined),
  expired: z.enum(["false"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/market/raporlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: MarketReportsRoute,
});

/**
 * Market raporları (Prompt 16 — web, aşama B), sekmeli: ciro + brüt kâr (istasyon / kategori / ürün; grafik + tablo
 * alternatifi), sepet ortalaması + akaryakıt → market dönüşüm oranı (tanımlar metinle), ölü stok, stok devir hızı ve
 * SKT yaklaşanlar. Hepsi Excel'e aktarılır. Ciro KDV HARİÇ ve defter toplamıdır (iptal karşı kaydı dâhil); SMM stok
 * hareketinden. Okuma `market.read`.
 */
function MarketReportsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketReports")}
      description={t("market.reports.description")}
      breadcrumb={[{ label: t("menu.marketReports") }]}
    >
      {({ companyId }) => <ReportsBody companyId={companyId} />}
    </MarketPage>
  );
}

function defaultRange(): { from: LocalDate; to: LocalDate } {
  const today = toIstanbulDate(new Date());
  return { from: addDays(today, -29), to: today };
}

function ReportsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const station = useMarketStations(companyId);
  const tab: MarketReportTab = search.tab ?? "sales";
  const fallback = defaultRange();
  const period: MarketReportPeriod = {
    stationId: station.stationId ?? undefined,
    from: search.from ?? fallback.from,
    to: search.to ?? fallback.to,
  };
  const groupBy: MarketReportGrouping = search.breakdown ?? "STATION";
  const usesPeriod = tab === "sales" || tab === "basket" || tab === "turnover";
  const usesGrouping = tab === "sales" || tab === "turnover";

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stations.length === 0) return <NoStations />;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <MarketStationField selection={station} />
        {usesPeriod ? (
          <DateRangePicker
            label={t("market.reports.period")}
            value={{ from: period.from as LocalDate, to: period.to as LocalDate }}
            onValueChange={(next) => setSearch({ from: next.from ?? undefined, to: next.to ?? undefined })}
          />
        ) : null}
        {usesGrouping ? (
          <FormField label={t("market.reports.groupBy")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={groupBy}
                onChange={(event) => setSearch({ breakdown: event.target.value })}
              >
                {MARKET_REPORT_GROUPINGS.map((value) => (
                  <option key={value} value={value}>
                    {t(`market.reports.grouping.${value}`)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        ) : null}
        {tab === "expiring" ? (
          <>
            <FormField
              label={t("market.reports.expiringDays")}
              description={t("market.reports.expiringDaysHint")}
              className="w-44"
            >
              {(control) => (
                <Input
                  {...control}
                  inputMode="numeric"
                  className="text-right tabular"
                  value={search.days === undefined ? "" : String(search.days)}
                  onChange={(event) => {
                    const text = event.target.value.trim();
                    setSearch({ days: /^\d{1,3}$/.test(text) ? Number(text) : undefined });
                  }}
                />
              )}
            </FormField>
            <label className="flex min-h-10 items-center gap-2 text-sm">
              <Checkbox
                checked={search.expired !== "false"}
                onChange={(event) => setSearch({ expired: event.target.checked ? undefined : "false" })}
              />
              {t("market.reports.includeExpired")}
            </label>
          </>
        ) : null}
      </Card>

      <Tabs className="grid gap-4" value={tab} onValueChange={(next) => setSearch({ tab: next })}>
        <TabsList aria-label={t("market.reports.tabsLabel")} className="flex-wrap">
          {MARKET_REPORT_TABS.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(`market.reports.tab.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        {/* İçerik MUTLAKA `TabsContent` içinde: tetikleyicinin `aria-controls` hedefi var olmalı. */}
        <TabsContent value={tab} className="grid gap-4 pt-0">
          {tab === "sales" ? <SalesTab companyId={companyId} period={period} groupBy={groupBy} /> : null}
          {tab === "basket" ? <BasketTab companyId={companyId} period={period} /> : null}
          {tab === "deadStock" ? <DeadStockTab companyId={companyId} stationId={period.stationId} /> : null}
          {tab === "turnover" ? (
            <TurnoverTab companyId={companyId} period={period} groupBy={groupBy} />
          ) : null}
          {tab === "expiring" ? (
            <ExpiringTab
              companyId={companyId}
              stationId={period.stationId}
              days={search.days}
              includeExpired={search.expired !== "false"}
            />
          ) : null}
        </TabsContent>
      </Tabs>
    </>
  );
}

function Totals({ children }: { children: ReactNode }): ReactElement {
  return <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</dl>;
}

function Total({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold">{children}</dd>
    </div>
  );
}

function ReportEmpty(): ReactElement {
  const { t } = useTranslation();
  return (
    <EmptyState
      headingLevel={2}
      icon={<ChartColumnBig />}
      title={t("market.reports.emptyTitle")}
      description={t("market.reports.emptyDescription")}
    />
  );
}

// ── Ciro + brüt kâr ──────────────────────────────────────────────────────────
function SalesTab({
  companyId,
  period,
  groupBy,
}: {
  companyId: string;
  period: MarketReportPeriod;
  groupBy: MarketReportGrouping;
}): ReactElement {
  const { t } = useTranslation();
  const report = useSalesReport(companyId, period, groupBy);
  const columns = useMemo<DataTableColumn<SalesReportRow>[]>(
    () => [
      {
        id: "label",
        header: t(`market.reports.grouping.${groupBy}`),
        hideable: false,
        exportValue: (row) => (row.code ? `${row.code} ${row.label}` : row.label),
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{row.label}</span>
            {row.code ? <span className="font-mono text-xs text-muted-foreground">{row.code}</span> : null}
          </span>
        ),
      },
      {
        id: "quantity",
        header: t("market.reports.fields.quantity"),
        align: "right",
        exportValue: (row) => quantityExport(row.quantity),
        cell: (row) => <span className="tabular">{formatStockQuantity(row.quantity)}</span>,
      },
      {
        id: "revenue",
        header: t("market.reports.fields.revenue"),
        align: "right",
        exportValue: (row) => moneyExport(row.revenue),
        cell: (row) => <MoneyText value={row.revenue} className="font-medium" />,
      },
      {
        id: "cogs",
        header: t("market.reports.fields.cogs"),
        align: "right",
        exportValue: (row) => moneyExport(row.cogs),
        cell: (row) => <MoneyText value={row.cogs} />,
      },
      {
        id: "grossProfit",
        header: t("market.reports.fields.grossProfit"),
        align: "right",
        exportValue: (row) => moneyExport(row.grossProfit),
        cell: (row) => <MoneyText value={row.grossProfit} colorize />,
      },
      {
        id: "margin",
        header: t("market.reports.fields.margin"),
        align: "right",
        exportValue: (row) => row.marginPercent,
        cell: (row) => <span className="tabular">{formatPercent(row.marginPercent)}</span>,
      },
    ],
    [t, groupBy],
  );

  if (report.error) return <Alert tone="danger">{errorMessage(report.error)}</Alert>;
  if (!report.data) return <SkeletonRows rows={6} />;
  const { rows, totals } = report.data;
  const chartRows = topByRevenue(rows);

  return (
    <>
      <Card className="grid gap-3 p-4">
        <h2 className="text-base font-semibold">{t("market.reports.totalsTitle")}</h2>
        <Totals>
          <Total label={t("market.reports.fields.revenue")}>
            <MoneyText value={totals.revenue} />
          </Total>
          <Total label={t("market.reports.fields.cogs")}>
            <MoneyText value={totals.cogs} />
          </Total>
          <Total label={t("market.reports.fields.grossProfit")}>
            <MoneyText value={totals.grossProfit} colorize />
          </Total>
          <Total label={t("market.reports.fields.margin")}>{formatPercent(totals.marginPercent)}</Total>
        </Totals>
        <p className="text-xs text-muted-foreground">{t("market.reports.salesDefinition")}</p>
      </Card>

      {rows.length === 0 ? (
        <ReportEmpty />
      ) : (
        <>
          <Card className="grid gap-4 p-4">
            <h2 className="text-base font-semibold">
              {rows.length > chartRows.length
                ? t("market.reports.salesChartTopTitle", { count: chartRows.length })
                : t("market.reports.salesChartTitle")}
            </h2>
            <ChartWithTable
              ariaLabel={t("market.reports.salesChartAria")}
              option={salesChartOption(rows, {
                revenue: t("market.reports.fields.revenue"),
                grossProfit: t("market.reports.fields.grossProfit"),
                amountAxis: t("market.reports.amountAxis"),
              })}
              height={Math.max(260, chartRows.length * 36 + 90)}
              tableLabel={t("market.reports.salesChartTable")}
            >
              <SimpleTable
                caption={t("market.reports.salesChartTable")}
                head={
                  <tr>
                    <th scope="col" className={thClass}>
                      {t(`market.reports.grouping.${groupBy}`)}
                    </th>
                    <th scope="col" className={thRightClass}>
                      {t("market.reports.fields.revenue")}
                    </th>
                    <th scope="col" className={thRightClass}>
                      {t("market.reports.fields.grossProfit")}
                    </th>
                  </tr>
                }
              >
                {chartRows.map((row) => (
                  <tr key={row.key} className="border-t">
                    <td className={tdClass}>{row.label}</td>
                    <td className={tdRightClass}>
                      <MoneyText value={row.revenue} />
                    </td>
                    <td className={tdRightClass}>
                      <MoneyText value={row.grossProfit} colorize />
                    </td>
                  </tr>
                ))}
              </SimpleTable>
            </ChartWithTable>
          </Card>
          <DataTable
            caption={t("market.reports.salesCaption")}
            columns={columns}
            rows={rows}
            rowKey={(row) => row.key}
            onExport={async (visible) => {
              await exportRowsToExcel({
                fileName: `market-ciro-${period.from}-${period.to}`,
                sheetName: t("market.reports.salesCaption"),
                columns: visible,
                rows,
              });
            }}
          />
        </>
      )}
    </>
  );
}

// ── Sepet ve dönüşüm ─────────────────────────────────────────────────────────
function BasketTab({ companyId, period }: { companyId: string; period: MarketReportPeriod }): ReactElement {
  const { t } = useTranslation();
  const report = useBasketReport(companyId, period);
  const columns = useMemo<DataTableColumn<BasketReportRow>[]>(
    () => [
      {
        id: "station",
        header: t("market.fields.station"),
        hideable: false,
        exportValue: (row) => row.stationName,
        cell: (row) => row.stationName,
      },
      {
        id: "saleCount",
        header: t("market.reports.fields.saleCount"),
        align: "right",
        exportValue: (row) => row.saleCount,
        cell: (row) => <span className="tabular">{formatCount(row.saleCount)}</span>,
      },
      {
        id: "marketSaleCount",
        header: t("market.reports.fields.marketSaleCount"),
        align: "right",
        exportValue: (row) => row.marketSaleCount,
        cell: (row) => <span className="tabular">{formatCount(row.marketSaleCount)}</span>,
      },
      {
        id: "marketNet",
        header: t("market.reports.fields.marketNet"),
        align: "right",
        exportValue: (row) => moneyExport(row.marketNet),
        cell: (row) => <MoneyText value={row.marketNet} />,
      },
      {
        id: "averageBasket",
        header: t("market.reports.fields.averageBasket"),
        align: "right",
        exportValue: (row) => moneyExport(row.averageBasket),
        cell: (row) => <MoneyText value={row.averageBasket} className="font-medium" />,
      },
      {
        id: "fuelSaleCount",
        header: t("market.reports.fields.fuelSaleCount"),
        align: "right",
        exportValue: (row) => row.fuelSaleCount,
        cell: (row) => <span className="tabular">{formatCount(row.fuelSaleCount)}</span>,
      },
      {
        id: "fuelWithMarketCount",
        header: t("market.reports.fields.fuelWithMarketCount"),
        align: "right",
        exportValue: (row) => row.fuelWithMarketCount,
        cell: (row) => <span className="tabular">{formatCount(row.fuelWithMarketCount)}</span>,
      },
      {
        id: "conversion",
        header: t("market.reports.fields.conversion"),
        align: "right",
        exportValue: (row) => row.conversionRatePercent,
        cell: (row) => (
          <span className="tabular font-medium">{formatPercent(row.conversionRatePercent)}</span>
        ),
      },
    ],
    [t],
  );

  if (report.error) return <Alert tone="danger">{errorMessage(report.error)}</Alert>;
  if (!report.data) return <SkeletonRows rows={6} />;
  const { rows, totals } = report.data;

  return (
    <>
      <Card className="grid gap-3 p-4">
        <h2 className="text-base font-semibold">{t("market.reports.totalsTitle")}</h2>
        <Totals>
          <Total label={t("market.reports.fields.averageBasket")}>
            <MoneyText value={totals.averageBasket} />
          </Total>
          <Total label={t("market.reports.fields.conversion")}>
            {formatPercent(totals.conversionRatePercent)}
          </Total>
          <Total label={t("market.reports.fields.marketSaleCount")}>
            {formatCount(totals.marketSaleCount)}
          </Total>
          <Total label={t("market.reports.fields.fuelSaleCount")}>{formatCount(totals.fuelSaleCount)}</Total>
        </Totals>
        <ul className="grid list-disc gap-1 ps-5 text-xs text-muted-foreground">
          <li>{t("market.reports.basketDefinition")}</li>
          <li>{t("market.reports.conversionDefinition")}</li>
        </ul>
      </Card>
      {rows.length === 0 ? (
        <ReportEmpty />
      ) : (
        <>
          <Card className="grid gap-4 p-4">
            <h2 className="text-base font-semibold">{t("market.reports.basketChartTitle")}</h2>
            <ChartWithTable
              ariaLabel={t("market.reports.basketChartAria")}
              option={basketChartOption(rows, {
                averageBasket: t("market.reports.fields.averageBasket"),
                conversion: t("market.reports.fields.conversion"),
                amountAxis: t("market.reports.amountAxis"),
                percentAxis: t("market.reports.percentAxis"),
              })}
              tableLabel={t("market.reports.basketChartTable")}
            >
              <SimpleTable
                caption={t("market.reports.basketChartTable")}
                head={
                  <tr>
                    <th scope="col" className={thClass}>
                      {t("market.fields.station")}
                    </th>
                    <th scope="col" className={thRightClass}>
                      {t("market.reports.fields.averageBasket")}
                    </th>
                    <th scope="col" className={thRightClass}>
                      {t("market.reports.fields.conversion")}
                    </th>
                  </tr>
                }
              >
                {rows.map((row) => (
                  <tr key={row.stationId} className="border-t">
                    <td className={tdClass}>{row.stationName}</td>
                    <td className={tdRightClass}>
                      <MoneyText value={row.averageBasket} />
                    </td>
                    <td className={tdRightClass}>{formatPercent(row.conversionRatePercent)}</td>
                  </tr>
                ))}
              </SimpleTable>
            </ChartWithTable>
          </Card>
          <DataTable
            caption={t("market.reports.basketCaption")}
            columns={columns}
            rows={rows}
            rowKey={(row) => row.stationId}
            onExport={async (visible) => {
              await exportRowsToExcel({
                fileName: `market-sepet-${period.from}-${period.to}`,
                sheetName: t("market.reports.basketCaption"),
                columns: visible,
                rows,
              });
            }}
          />
        </>
      )}
    </>
  );
}

// ── Ölü stok ─────────────────────────────────────────────────────────────────
function DeadStockTab({
  companyId,
  stationId,
}: {
  companyId: string;
  stationId: string | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const report = useDeadStockReport(companyId, stationId);
  const columns = useMemo<DataTableColumn<DeadStockRow>[]>(
    () => [
      {
        id: "product",
        header: t("market.fields.product"),
        hideable: false,
        exportValue: (row) => `${row.productCode} ${row.productName}`,
        cell: (row) => (
          <Link
            to="/market/stok/$productId"
            params={{ productId: row.productId }}
            search={{ station: row.stationId }}
            className="grid gap-0.5 underline underline-offset-4"
          >
            <span>{row.productName}</span>
            <span className="font-mono text-xs text-muted-foreground">
              {row.productCode}
              {row.categoryName ? ` · ${row.categoryName}` : ""}
            </span>
          </Link>
        ),
      },
      {
        id: "warehouse",
        header: t("market.fields.warehouse"),
        exportValue: (row) => `${row.stationName} · ${row.warehouseCode}`,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{row.stationName}</span>
            <span className="font-mono text-xs text-muted-foreground">{row.warehouseCode}</span>
          </span>
        ),
      },
      {
        id: "quantity",
        header: t("market.fields.onHand"),
        align: "right",
        exportValue: (row) => quantityExport(row.quantity),
        cell: (row) => <span className="tabular">{formatStockQuantity(row.quantity)}</span>,
      },
      {
        id: "value",
        header: t("market.fields.stockValue"),
        align: "right",
        exportValue: (row) => moneyExport(row.value),
        cell: (row) => <MoneyText value={row.value} />,
      },
      {
        id: "lastSale",
        header: t("market.fields.lastSaleAt"),
        exportValue: (row) => row.lastSaleDate,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span className="tabular">
              {row.lastSaleDate ? formatDate(row.lastSaleDate) : t("market.reports.neverSold")}
            </span>
            {row.daysSinceLastSale !== null ? (
              <span className="text-xs text-muted-foreground">
                {t("market.reports.daysSince", { count: row.daysSinceLastSale })}
              </span>
            ) : null}
          </span>
        ),
      },
      {
        id: "threshold",
        header: t("market.reports.fields.deadStockDays"),
        align: "right",
        defaultVisible: false,
        exportValue: (row) => row.deadStockDays,
        cell: (row) => <span className="tabular">{formatCount(row.deadStockDays)}</span>,
      },
    ],
    [t],
  );

  if (report.error) return <Alert tone="danger">{errorMessage(report.error)}</Alert>;
  if (!report.data) return <SkeletonRows rows={6} />;
  const { rows, totals, asOf } = report.data;
  return (
    <>
      <Card className="grid gap-3 p-4">
        <Totals>
          <Total label={t("market.reports.fields.lineCount")}>{formatCount(totals.lineCount)}</Total>
          <Total label={t("market.fields.stockValue")}>
            <MoneyText value={totals.value} />
          </Total>
          <Total label={t("market.reports.asOf")}>{formatDate(asOf)}</Total>
        </Totals>
        <p className="text-xs text-muted-foreground">{t("market.reports.deadStockDefinition")}</p>
      </Card>
      <DataTable
        caption={t("market.reports.deadStockCaption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => `${row.warehouseId}:${row.productId}`}
        emptyState={<ReportEmpty />}
        onExport={async (visible) => {
          await exportRowsToExcel({
            fileName: `market-olu-stok-${asOf}`,
            sheetName: t("market.reports.deadStockCaption"),
            columns: visible,
            rows,
          });
        }}
      />
    </>
  );
}

// ── Stok devir hızı ──────────────────────────────────────────────────────────
function TurnoverTab({
  companyId,
  period,
  groupBy,
}: {
  companyId: string;
  period: MarketReportPeriod;
  groupBy: MarketReportGrouping;
}): ReactElement {
  const { t } = useTranslation();
  const report = useTurnoverReport(companyId, period, groupBy);
  const columns = useMemo<DataTableColumn<TurnoverRow>[]>(
    () => [
      {
        id: "label",
        header: t(`market.reports.grouping.${groupBy}`),
        hideable: false,
        exportValue: (row) => (row.code ? `${row.code} ${row.label}` : row.label),
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{row.label}</span>
            {row.code ? <span className="font-mono text-xs text-muted-foreground">{row.code}</span> : null}
          </span>
        ),
      },
      {
        id: "cogs",
        header: t("market.reports.fields.cogs"),
        align: "right",
        exportValue: (row) => moneyExport(row.cogs),
        cell: (row) => <MoneyText value={row.cogs} />,
      },
      {
        id: "opening",
        header: t("market.reports.fields.openingValue"),
        align: "right",
        exportValue: (row) => moneyExport(row.openingValue),
        cell: (row) => <MoneyText value={row.openingValue} />,
      },
      {
        id: "closing",
        header: t("market.reports.fields.closingValue"),
        align: "right",
        exportValue: (row) => moneyExport(row.closingValue),
        cell: (row) => <MoneyText value={row.closingValue} />,
      },
      {
        id: "average",
        header: t("market.reports.fields.averageValue"),
        align: "right",
        exportValue: (row) => moneyExport(row.averageValue),
        cell: (row) => <MoneyText value={row.averageValue} />,
      },
      {
        id: "turnover",
        header: t("market.reports.fields.turnover"),
        align: "right",
        exportValue: (row) => row.turnover,
        cell: (row) => <span className="tabular font-medium">{formatRatio(row.turnover)}</span>,
      },
      {
        id: "daysOnHand",
        header: t("market.reports.fields.daysOnHand"),
        align: "right",
        exportValue: (row) => row.daysOnHand,
        cell: (row) => <span className="tabular">{formatRatio(row.daysOnHand, 1)}</span>,
      },
    ],
    [t, groupBy],
  );

  if (report.error) return <Alert tone="danger">{errorMessage(report.error)}</Alert>;
  if (!report.data) return <SkeletonRows rows={6} />;
  const { rows, totals, periodDays } = report.data;
  return (
    <>
      <Card className="grid gap-3 p-4">
        <Totals>
          <Total label={t("market.reports.fields.turnover")}>{formatRatio(totals.turnover)}</Total>
          <Total label={t("market.reports.fields.daysOnHand")}>{formatRatio(totals.daysOnHand, 1)}</Total>
          <Total label={t("market.reports.fields.cogs")}>
            <MoneyText value={totals.cogs} />
          </Total>
          <Total label={t("market.reports.fields.averageValue")}>
            <MoneyText value={totals.averageValue} />
          </Total>
        </Totals>
        <p className="text-xs text-muted-foreground">
          {t("market.reports.turnoverDefinition", { days: periodDays })}
        </p>
      </Card>
      <DataTable
        caption={t("market.reports.turnoverCaption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.key}
        emptyState={<ReportEmpty />}
        onExport={async (visible) => {
          await exportRowsToExcel({
            fileName: `market-devir-hizi-${period.from}-${period.to}`,
            sheetName: t("market.reports.turnoverCaption"),
            columns: visible,
            rows,
          });
        }}
      />
    </>
  );
}

// ── SKT yaklaşanlar ──────────────────────────────────────────────────────────
function ExpiringTab({
  companyId,
  stationId,
  days,
  includeExpired,
}: {
  companyId: string;
  stationId: string | undefined;
  days: number | undefined;
  includeExpired: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const report = useExpiringLotsReport(companyId, stationId, { days, includeExpired });
  const columns = useMemo<DataTableColumn<ExpiringLotRow>[]>(
    () => [
      {
        id: "product",
        header: t("market.fields.product"),
        hideable: false,
        exportValue: (row) => `${row.productCode} ${row.productName}`,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{row.productName}</span>
            <span className="font-mono text-xs text-muted-foreground">{row.productCode}</span>
          </span>
        ),
      },
      {
        id: "lot",
        header: t("market.fields.lotNo"),
        exportValue: (row) => row.lotNo,
        cell: (row) => <span className="font-mono text-xs">{row.lotNo ?? t("market.lots.noLotNo")}</span>,
      },
      {
        id: "warehouse",
        header: t("market.fields.warehouse"),
        exportValue: (row) => `${row.stationName} · ${row.warehouseCode}`,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{row.stationName}</span>
            <span className="font-mono text-xs text-muted-foreground">{row.warehouseCode}</span>
          </span>
        ),
      },
      {
        id: "expiry",
        header: t("market.fields.expiryDate"),
        exportValue: (row) => row.expiryDate,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span className="tabular">{formatDate(row.expiryDate)}</span>
            <span className="text-xs text-muted-foreground">
              {row.daysToExpiry < 0
                ? t("market.lots.daysAgo", { count: -row.daysToExpiry })
                : t("market.lots.daysLeft", { count: row.daysToExpiry })}
            </span>
          </span>
        ),
      },
      {
        id: "state",
        header: t("market.fields.expiryState"),
        exportValue: (row) => t(`market.reports.expiryState.${row.state}`),
        cell: (row) => (
          <span
            className={row.state === "EXPIRED" ? "font-medium text-destructive" : "font-medium text-warning"}
          >
            {t(`market.reports.expiryState.${row.state}`)}
          </span>
        ),
      },
      {
        id: "quantity",
        header: t("market.fields.quantity"),
        align: "right",
        exportValue: (row) => quantityExport(row.quantity),
        cell: (row) => <span className="tabular">{formatStockQuantity(row.quantity)}</span>,
      },
      {
        id: "value",
        header: t("market.reports.fields.estimatedValue"),
        align: "right",
        exportValue: (row) => moneyExport(row.estimatedValue),
        cell: (row) => <MoneyText value={row.estimatedValue} />,
      },
    ],
    [t],
  );

  if (report.error) return <Alert tone="danger">{errorMessage(report.error)}</Alert>;
  if (!report.data) return <SkeletonRows rows={6} />;
  const { rows, totals, asOf } = report.data;
  return (
    <>
      <Card className="grid gap-3 p-4">
        <Totals>
          <Total label={t("market.reports.fields.lineCount")}>{formatCount(totals.lineCount)}</Total>
          <Total label={t("market.fields.quantity")}>{formatStockQuantity(totals.quantity)}</Total>
          <Total label={t("market.reports.fields.estimatedValue")}>
            <MoneyText value={totals.estimatedValue} />
          </Total>
          <Total label={t("market.reports.asOf")}>{formatDate(asOf)}</Total>
        </Totals>
      </Card>
      <DataTable
        caption={t("market.reports.expiringCaption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.lotId}
        emptyState={<ReportEmpty />}
        onExport={async (visible) => {
          await exportRowsToExcel({
            fileName: `market-skt-${asOf}`,
            sheetName: t("market.reports.expiringCaption"),
            columns: visible,
            rows,
          });
        }}
      />
    </>
  );
}
